import React, { useEffect, useRef } from 'react'
import * as fabric from 'fabric'
import QuaterService, { QuaterData } from '@/service/QuaterService'

interface Props {
    quaters: QuaterData[]
    width: number
    height: number
    onQuaterUpdate: (q: QuaterData) => void
}

const RightOperationBoard = ({ quaters, width, height, onQuaterUpdate }: Props) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const fabricRef = useRef<fabric.Canvas | null>(null)

    const randomInt = (min: number, max: number) =>
        Math.floor(Math.random() * (max - min + 1)) + min;

    const getBasePositionByPosition1 = (
        position: string | null | undefined,
        index: number
    ) => {
        let baseX = 20

        let baseY = height / 2

        if (position === 'GK') {
            baseY = height - height / 12
            baseX = width / 2 - 20
        } else if (position === 'CF') {
            baseY = height / 4
        }  else if (position === 'WF') {
            baseY = height / 3
        } else if (position === 'MF') {
            baseY = height / 2
        } else if (position === 'SB') {
            baseY = height - height / 3
        } else if (position === 'CB') {
            baseY = height - height / 4
        }

        return {
            x: position === 'GK'?baseX:(baseX + (index * 15)),
            y: baseY
        }
    }

    /** 1️⃣ 초기 캔버스 생성 */
    useEffect(() => {
        if (!canvasRef.current) return

        const canvas = new fabric.Canvas(canvasRef.current, {
            width,
            height,
            backgroundColor: '#15803d', // 잔디
            selection: false
        })

        fabricRef.current = canvas
        drawField(canvas)

        return () => {
            canvas.dispose()
            fabricRef.current = null
        }
    }, [])

    /** 2️⃣ width / height 변경 시 캔버스 리사이즈 */
    useEffect(() => {
        if (!fabricRef.current) return
        const canvas = fabricRef.current

        canvas.setWidth(width)
        canvas.setHeight(height)

        canvas.clear()
        canvas.backgroundColor = '#15803d'

        drawField(canvas)
        quaters.forEach(addPlayer)

        canvas.renderAll()
    }, [width, height])

    /** 3️⃣ quaters 변경 시 선수 다시 그림 */
    useEffect(() => {
        if (!fabricRef.current) return
        const canvas = fabricRef.current

        canvas.getObjects('group').forEach(o => canvas.remove(o))
        quaters.forEach(addPlayer)

        canvas.renderAll()
    }, [quaters])

    /** ⚽ 축구장 */
    const drawField = (canvas: fabric.Canvas) => {
        const goalWidth = width / 4
        const goalHeight = height / 15
        canvas.add(
            new fabric.Rect({
                left: 5,
                top: 5,
                width: width - 10,
                height: height - 10,
                stroke: 'white',
                strokeWidth: 4,
                fill: 'transparent',
                selectable: false,
                evented: false
            }),
            new fabric.Line(
                [5, height / 2, width - 5, height / 2],
                {
                    stroke: 'white',
                    strokeWidth: 2,
                    selectable: false,
                    evented: false
                }
            ),
            new fabric.Circle({
                left: width / 2 - 50,
                top: height / 2 - 50,
                radius: 50,
                stroke: 'white',
                strokeWidth: 3,
                fill: 'transparent',
                selectable: false,
                evented: false
            }),


        /** 🥅 상단 골대 */
            new fabric.Rect({
                left: width / 2 - goalWidth / 2,
                top: 5,
                width: goalWidth,
                height: goalHeight,
                stroke: 'white',
                strokeWidth: 3,
                fill: 'transparent',
                selectable: false,
                evented: false
            }),

            /** 🥅 하단 골대 */
            new fabric.Rect({
                left: width / 2 - goalWidth / 2,
                top: height - goalHeight - 5,
                width: goalWidth,
                height: goalHeight,
                stroke: 'white',
                strokeWidth: 3,
                fill: 'transparent',
                selectable: false,
                evented: false
            })

        )
    }

    /** 👕 선수 추가 */
    const addPlayer = (q: QuaterData) => {
        const index = quaters.findIndex(item => item.id === q.id)

        if (!fabricRef.current) return
        const canvas = fabricRef.current

        const color =
            q.playerTeamABType === 'A'
                ? q.status === '교체'
                    ? 'yellow'
                    : '#ef4444'
                : q.status === '교체'
                    ? 'yellow'
                    : '#2563eb'

        const circle = new fabric.Circle({
            radius: 23,
            fill: color,
            originX: 'center',
            originY: 'center'
        })

        const label = new fabric.Text(
            `${q.playerName}\n${q.playerPosition1}${q.playerPosition2 ? `/${q.playerPosition2}` : ''}`,
        {
                fontSize: 14,
                fontWeight: 700,
                fill: 'black',
                textAlign: 'center',
                originX: 'center',
                originY: 'center',
                top:0
            }
        )

        const basePos =
            q.pointX != null && q.pointY != null
                ? { x: percentToPxX(q.pointX), y: percentToPxY(q.pointY) }
                : getBasePositionByPosition1(q.playerPosition1, index)

        const group = new fabric.Group([circle, label], {
            left: basePos.x,
            top: basePos.y,
            hasControls: false,
            lockScalingX: true,
            lockScalingY: true
        })

        /** 드래그 후 위치 저장 */
        group.on('mouseup', async () => {
            const { left, top } = group
            if (left == null || top == null) return

            await QuaterService.updateQuater(q.id, {
                status: q.status,
                pointX: pxToPercentX(Math.round(left)),
                pointY: pxToPercenY(Math.round(top))
            })

            const updatedQuater: QuaterData = {
                ...q,
                pointX: pxToPercentX(Math.round(left)),
                pointY: pxToPercenY(Math.round(top))
            }

            onQuaterUpdate(updatedQuater)
        })

        canvas.add(group)
    }


    /** px → % */
    const pxToPercentX = (x: number): number => {
        return +(x / width * 100).toFixed(2)
    }

    const pxToPercenY = (y: number) => {
        return +(y / height * 100).toFixed(2)
    }

    /** % → px */
    const percentToPxX = (xRate: number) => {
        return  Math.round((xRate / 100) * width)
    }

    const percentToPxY = (yRate: number) => {
        return Math.round((yRate / 100) * height)
    }

    return (
        <canvas
            ref={canvasRef}
            style={{ display: 'block' }}
        />
    )
}

export default RightOperationBoard
